import { useEffect, useRef } from "react";
import { Button } from "../ui";

type InfoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
};

export function InfoModal({ isOpen, onClose, title, children }: InfoModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-8">
      <article
        className="w-full max-w-2xl rounded-3xl border border-border-subtle bg-background-card text-foreground shadow-layer"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
          <h3 className="text-title font-semibold">{title}</h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 rounded-full border border-border-subtle p-0 text-foreground-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            onClick={onClose}
          >
            ×
          </Button>
        </header>
        <div className="space-y-4 px-6 py-5 text-body-sm text-foreground">
          {children}
        </div>
        <footer className="flex justify-end border-t border-border-subtle px-6 py-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </footer>
      </article>
    </div>
  );
}
